<template>
  <el-card :class="[`dashboard-table-view--${entityType}`]" class="dashboard-table-view">
    <template v-for="(entity, index) in entities" :key="'row_'+index">
      <div class="dashboard-table-view__row row" @click="goToHome(entity)">
        <sample-ribbon v-if="$permissions.isSampleInternalProjectID(entity.id)" size="small" />
        <div class="dashboard-table-view__item">
          <div class="col-11 col-md-6 dashboard-table-view__item-title">
            <i :class="isMailbox ? 'ddi-mailbox' : 'ddi-project'" class="ddi" />
            <span>{{entity.name}}</span>
          </div>
          <div class="d-none d-md-flex col-md-3 dashboard-table-view__item-counts">
            <div>
              <template v-if="entity.activeCount">
                <router-link :to="getActiveRoute(entity)" class="content-summary-counter" @click.prevent.stop>{{entity.activeCount}} active</router-link>
                <template v-if="entity.assignedCount">
                  & <router-link :to="getAssignedToYouRoute(entity)" class="content-summary-counter" @click.prevent.stop>{{entity.assignedCount}} to you</router-link>
                </template>
              </template>
              <template v-else>
                <router-link :to="getActiveRoute(entity)" @click.prevent.stop>Nothing active</router-link>
              </template>
            </div>
          </div>
          <div class="d-none d-md-flex col-md-2 dashboard-table-view__item-updated">
            <span>{{ entity.lastUpdate }}</span>
          </div>
          <div class="col-1 col-md-1 dashboard-table-view__item-options">
            <router-link v-if="$permissions.isNotGuest && !$permissions.isAppSumoAccount && entityType === 'projects'" :to="getScheduleRoute(entity)" @click.prevent.stop>
              <i class="ddi ddi-clock" />
            </router-link>
            <favorite-button 
              :is-favorite="entity.isFavorite" 
              @click.prevent.stop="$emit('favorite-clicked', entity)"
            />
          </div>
        </div>
      </div>
      <div v-if="index < entities.length - 1" class="divider" />
    </template>
  </el-card>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */
import EntityTypeMixin from '@/mixins/entity-type.mixin'

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { DefaultFilterIDs, EEntities } from '@/types'
import { IProjectsOutputProjectForHome } from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'DashboardTableView',

  extends: EntityTypeMixin,

  props: {
    entities: {
      type: Array as () => Array<IProjectsOutputProjectForHome>,
      required: true,
    },
  },

  emits: ['favorite', 'favorite-clicked'],

  computed: {
    isMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
  },

  methods: {
    handleFavorite(entity: any) {
      this.$emit('favorite', entity, entity.isFavorite)
    },
    goToHome(entity: any) {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.goToMailbox(
            this.$router,
            this.$route.params.accountId,
            entity.id,
            entity.name,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.goToProjectLanding(
            this.$router,
            this.$route.params.accountId,
            entity.id,
            this.$permissions,
            entity.name,
          )
      }
    },
    getScheduleRoute(entity: any) {
      return RouteAssistant.getProjectScheduleRoute(
        this.$route.params.accountId,
        entity.id,
      )
    },
    getActiveRoute(entity: any) {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxLandingRoute(
            this.$route.params.accountId,
            entity.id,
            entity.name,
            DefaultFilterIDs.ACTIVE,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            entity.id,
            this.$permissions,
            entity.name,
            DefaultFilterIDs.ACTIVE,
          )
      }
    },
    getAssignedToYouRoute(entity: any) {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxLandingRoute(
            this.$route.params.accountId,
            entity.id,
            entity.name,
            DefaultFilterIDs.ASSIGNED_TO_YOU,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            entity.id,
            this.$permissions,
            entity.name,
            DefaultFilterIDs.ASSIGNED_TO_YOU,
          )
      }
    },
  },
})
</script>

<style lang="scss">
.dashboard-table-view {
  .el-card__body {
    padding: 0;
  }

  .sample-ribbon {
    margin-top: 12px;
    margin-left: 10px;
  }

  &__row {
    &:hover {
      cursor: pointer;
      background-color: var(--icon-hover-bg);
    }
  }

  &__item {
    width: 100%;
    display: flex;
    padding: 10px;

    > div {
      display: flex;
      align-items: center;
    }

    &-title {
      @include lettering--p;
      font-weight: $bold;

      > i {
        margin-right: 8px;
        color: var(--color-text-third);
      }
    }

    &-counts {
      @include lettering--p-sm;

      .content-summary-counter {
        font-weight: $bold;
      }

      a {
        color: var(--color-text-secondary);

        &:hover {
          color: inherit;
        }
      }
    }

    &-updated {
      @include lettering--p-sm;
      color: var(--color-text-third);
    }

    &-options {
      justify-content: flex-end !important;

      > * {
        padding-left: 4px;
      }

      i {
        color: var(--color-text-third);
      }
    }
  }

  &--mailboxes {
    .content-summary-counter {
      color: $mailboxes--theme-color;
      &:hover {
        color: $mailboxes--theme-color;
      }
    }
  }

  &--projects {
    .content-summary-counter {
      color: $projects--theme-color;
      &:hover {
        color: $projects--theme-color;
      }
    }
  }

  .divider {
    width: 100%;
    height: 1px;
    background-color: $silver-two;
  }
}
</style>
